<template>
  <div>
    <x-heading size="4">Elevation</x-heading>
    <x-elevation-profile
      v-if="elevation"
      :class="['elevation-profile', { locked: lockedContent }]"
      :distance="length"
      :points="averagedElevation"
      :geobuf-string="geobufString"
      :line-style="lineStyle"
      :gain="gain"
      :route-geo-json="geojson"
    />
    <div v-else class="elevation-profile">
      <x-loading-spinner v-if="loading" />
      <template v-else>Elevation profile not available</template>
    </div>
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
import distance from '@turf/distance'

import { requestElevation } from '@/services/elevation-service'
import markupService from '@/services/markup-service'
import { isRoute } from '@/services/markup-service/helpers'

import { LINES } from '@/lib/types'
import { arraySample, findLineDistance } from '@/lib/math'

import XElevationProfile from '@/components/elevationProfile/x-elevation-profile'
import XLoadingSpinner from '@/components/shared/x-loading-spinner'
import xHeading from '@/components/shared/x-heading'

const TARGET_SAMPLE_COUNT = 100

/**
 * Calculate elevation gain/loss, reducing the noise
 * Elevation differences between points less than threshold will be ignored
 * Details here - https://www.gpsvisualizer.com/tutorials/elevation_gain.html
 *
 * @param points List of elevation sample points (in meters)
 * @returns Object with two properties: `gain` and `loss`
 * */
export function calculateGain(points) {
  const maxElevation = Math.max(...points)
  const minElevation = Math.min(...points)
  const threshold = maxElevation - minElevation < 500 ? 5 : 10

  let lastValid = points[0]
  let gain = 0
  let loss = 0

  for (let i = 1; i < points.length; i++) {
    const point = points[i]
    const difference = Math.abs(point - lastValid)

    if (difference > threshold) {
      point > lastValid ? (gain += difference) : (loss += difference)
      lastValid = point
    }
  }

  return { gain, loss }
}

export function getWeights(coords) {
  const lengths = getSegmentLengths(coords)
  const distance = lengths.reduce((a, b) => a + b)
  return lengths.map((length) => length / distance)
}

export function coordsOnLine(coords, count = TARGET_SAMPLE_COUNT) {
  const weights = getWeights(coords)
  const fineCoords = []
  count = Math.max(count, coords.length)
  coords.reduce((prev, next, index) => {
    const currentCount = Math.round(count * weights[index - 1])
    const stepX = (next[0] - prev[0]) / currentCount
    const stepY = (next[1] - prev[1]) / currentCount

    for (let i = 0; i < currentCount; i++) {
      const x = prev[0] + i * stepX
      const y = prev[1] + i * stepY
      fineCoords.push([parseFloat(x.toFixed(6)), parseFloat(y.toFixed(6))])
    }

    return next
  })

  return fineCoords
}

export function getSegmentLengths(coords) {
  const lengths = []
  coords.reduce((prev, next) => {
    lengths.push(distance(prev, next, { units: 'meters' }))
    return next
  })
  return lengths
}

export default {
  name: 'ElevationProfileContainer',
  components: {
    XElevationProfile,
    XLoadingSpinner,
    xHeading,
  },
  /**
   * Component can either receive coordinates (from a user-generated line) to transform to an Array of points
   * Or the Array of points ready to go (from Rich Content service)
   */
  props: {
    coordinates: {
      type: Array,
      default: undefined,
    },
    elevationData: {
      type: Object,
      default: undefined,
    },
    lockedContent: {
      type: Boolean,
      required: false,
    },
    useExactCoords: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['statsReady'],
  data() {
    return {
      elevation: null,
      loading: true,
      length: 0,
      geobufString: '',
      lineStyle: LINES.SOLID,
      gain: null,
      geojson: null,
    }
  },
  computed: {
    averagedElevation: function () {
      if (this.elevation.length > TARGET_SAMPLE_COUNT) {
        return arraySample(this.elevation, TARGET_SAMPLE_COUNT)
      }
      return this.elevation
    },
  },
  watch: {
    coordinates: function () {
      this.retrieveElevation()
    },
    elevationData: function () {
      this.handleElevData()
    },
  },
  mounted() {
    // If we received the array from Rich Content, process that
    // Else if we receive coordinates, convert them to the necessary elevation array
    this.elevationData ? this.handleElevData() : this.retrieveElevation()
  },
  methods: {
    /**
     * Take incoming coordinates from user markup, generate elevation array and stats from them
     */
    async retrieveElevation() {
      const markup = markupService.getFocusedMarkup()?.markup
      if (!markup) {
        this.loading = false
        return
      }

      let markupStyle
      if (isRoute(markup)) {
        markupStyle = markup?.style
      } else {
        markupStyle = markup?.properties?.style
      }
      this.lineStyle = markupStyle ?? LINES.SOLID

      this.elevation = null
      this.$emit('statsReady', null)

      // Comparing the length of the coordinates arrays on the focused markup
      // and the current prop ensures elevation is only being requested when those
      // match
      const markupCoords = markup?.coordinates
      if (
        !this.coordinates ||
        !this.coordinates.length ||
        markupCoords?.length !== this.coordinates.length
      ) {
        this.loading = false
        return
      }

      this.loading = true

      const coords = coordsOnLine(this.coordinates)

      try {
        this.length = findLineDistance(this.coordinates)
        this.elevation = await requestElevation(coords, this.useExactCoords)
        const { gain, loss } = calculateGain(this.elevation)
        this.gain = gain
        this.$emit('statsReady', {
          'Elevation gain': gain,
          'Elevation loss': loss,
        })
      } catch (e) {
        // Safe to ignore
      }

      this.loading = false
    },

    /**
     * Process incoming array of elevation points from Rich Content and generate stats
     */
    handleElevData() {
      const { elevationPreview, lengthInMeters, geobufString, geojson } =
        this.elevationData
      this.elevation = elevationPreview
      this.geobufString = geobufString
      this.geojson = geojson
      this.lineStyle = LINES.GRADIENT
      this.length = lengthInMeters
      this.$emit('statsReady', null)
      try {
        const { gain, loss } = calculateGain(this.elevation)
        this.gain = gain
        this.$emit('statsReady', {
          'Elevation gain': gain,
          'Elevation loss': loss,
        })
      } catch (e) {
        // Safe to ignore
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/system/_colors.scss';

.elevation-profile {
  display: flex;
  justify-content: center;
  align-items: center;

  &.locked {
    filter: blur(3px);
    pointer-events: none;
  }

  &__container {
    position: relative;
    padding-top: 15px;
    overflow: hidden;
  }
}
</style>
